import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';

const THEME_KEY = 'duckers-theme';
const VALID_THEMES = ['blue', 'orange', 'red'];
const DEFAULT_THEME = 'blue';

const ThemeContext = createContext({ theme: DEFAULT_THEME, setTheme: () => {} });

function loadSavedTheme() {
  try {
    const saved = localStorage.getItem(THEME_KEY);
    if (saved && VALID_THEMES.includes(saved)) return saved;
  } catch { /* localStorage unavailable */ }
  return DEFAULT_THEME;
}

function applyThemeToDOM(theme) {
  const root = document.documentElement;
  root.classList.remove(...VALID_THEMES.map(t => `theme-${t}`));
  root.classList.add(`theme-${theme}`);
  root.setAttribute('data-theme', theme);
}

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(loadSavedTheme);

  useEffect(() => {
    applyThemeToDOM(theme);
  }, [theme]);

  const setTheme = useCallback((next) => {
    const resolved = typeof next === 'function' ? next(theme) : next;
    if (!VALID_THEMES.includes(resolved)) return;
    try { localStorage.setItem(THEME_KEY, resolved); } catch { /* noop */ }
    setThemeState(resolved);
  }, [theme]);

  const value = useMemo(() => ({ theme, setTheme }), [theme, setTheme]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext); 